<template>
  <div class="overlay">
    <ul>
      <li class="icon-container" @mouseenter="startReveal" @mouseleave="resetReveal">
        <a href="/Projects" class="icon-link">
          <!-- Icon Element -->
          <div class="icon-wrapper" :class="{ 'hide-icon': isRevealed }">
            <svg class="animated-icon" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <!-- Clipboard background -->
              <rect x="4" y="2" width="14" height="18" rx="2" ry="2" 
                    fill="none" stroke="currentColor" stroke-width="2" 
                    class="clipboard"/>
              
              <!-- Clipboard clip -->
              <rect x="8" y="0" width="6" height="4" rx="1" ry="1" 
                    fill="none" stroke="currentColor" stroke-width="2" 
                    class="clip"/>
              
              <!-- Check marks -->
              <g class="checkmarks">
                <polyline points="9,11 11,13 15,9" 
                         fill="none" stroke="currentColor" stroke-width="2" 
                         stroke-linecap="round" stroke-linejoin="round"
                         class="check check-1"/>
                <polyline points="9,15 11,17 15,13" 
                         fill="none" stroke="currentColor" stroke-width="2" 
                         stroke-linecap="round" stroke-linejoin="round"
                         class="check check-2"/>
              </g>
            </svg>
          </div>
          
          <!-- Hidden Text Element -->
          <div class="text-wrapper" :class="{ 'show-text': isRevealed }">
            <span class="reveal-text">My Projects</span>
          </div>
        </a>
      </li>
    </ul>
  </div>
</template>

<script setup>
import { ref } from 'vue'

const isRevealed = ref(false)

const startReveal = () => {
  isRevealed.value = true
}

const resetReveal = () => {
  isRevealed.value = false
}
</script>

<style scoped>
* {
  overflow: visible;
}

.overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1;
}

ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.icon-container {
  width: 11rem;
  height: 11rem;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 3rem;
  color: white;
  position: relative;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  margin: 0.5rem;
  cursor: pointer;
  overflow: hidden;
}

.icon-container:hover {
  border-color: rgba(255, 255, 255, 0.6);
  background: rgba(255, 255, 255, 0.2);
  transform: scale(1.05);
  box-shadow: 
    0 10px 30px rgba(0, 0, 0, 0.3),
    0 0 20px rgba(255, 255, 255, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.2);
}

.icon-link {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  text-decoration: none;
  color: inherit;
  position: relative;
}

/* Icon Wrapper Styles */
.icon-wrapper {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  opacity: 1;
  transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 2;
}

.icon-wrapper.hide-icon {
  opacity: 0;
  transform: translate(-50%, -50%) scale(0.3) rotate(180deg);
  pointer-events: none;
}

.animated-icon {
  width: 4rem;
  height: 4rem;
  transition: all 0.4s ease;
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3));
  color: #ffffff;
}

/* Text Wrapper Styles */
.text-wrapper {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) scale(0.5);
  opacity: 0;
  transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  text-align: center;
  z-index: 1;
  pointer-events: none;
}

.text-wrapper.show-text {
  opacity: 1;
  transform: translate(-50%, -50%) scale(1);
  pointer-events: auto;
  transition-delay: 0.2s;
}

.reveal-text {
  display: block;
  font-size: 1.2rem;
  font-weight: 600;
  color: white;
  margin-bottom: 0.5rem;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
  animation: textSlideIn 0.6s ease-out;
}

.reveal-subtext {
  display: block;
  font-size: 0.9rem;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.8);
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
  animation: textSlideIn 0.6s ease-out 0.1s both;
}

/* Animations */
@keyframes textSlideIn {
  0% {
    opacity: 0;
    transform: translateY(20px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes iconFadeOut {
  0% {
    opacity: 1;
    transform: scale(1) rotate(0deg);
  }
  50% {
    opacity: 0.5;
    transform: scale(0.8) rotate(90deg);
  }
  100% {
    opacity: 0;
    transform: scale(0.3) rotate(180deg);
  }
}

@keyframes textFadeIn {
  0% {
    opacity: 0;
    transform: scale(0.5) translateY(20px);
  }
  100% {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

/* SVG Animation Effects (Enhanced) */
.clipboard {
  transition: all 0.3s ease;
}

.clip {
  transition: all 0.3s ease 0.1s;
}

.checkmarks {
  transition: all 0.3s ease;
}

.check {
  stroke-dasharray: 20;
  stroke-dashoffset: 20;
  transition: stroke-dashoffset 0.6s ease;
}

/* Enhanced Hover Effects */
.icon-container:hover .animated-icon {
  transform: scale(1.1);
  filter: drop-shadow(0 6px 12px rgba(0, 0, 0, 0.4));
}

.icon-container:hover .clipboard {
  stroke-width: 2.5;
}

.icon-container:hover .clip {
  stroke-width: 2.5;
}

.icon-container:hover .check-1 {
  stroke-dashoffset: 0;
  transition-delay: 0.2s;
}

.icon-container:hover .check-2 {
  stroke-dashoffset: 0;
  transition-delay: 0.4s;
}

/* Responsive Design */
@media (max-width: 950px) {
  .icon-container {
    width: 20vw;
    height: 20vw;
    margin: 0.5vw;
    font-size: 2.5rem;
  }
  
  .animated-icon {
    width: 3rem;
    height: 3rem;
  }
  
  .reveal-text {
    font-size: 1rem;
  }
  
  .reveal-subtext {
    font-size: 0.8rem;
  }
}

@media (max-width: 480px) {
  .icon-container {
    font-size: 2rem;
  }
  
  .animated-icon {
    width: 2.5rem;
    height: 2.5rem;
  }
  
  .reveal-text {
    font-size: 0.9rem;
  }
  
  .reveal-subtext {
    font-size: 0.7rem;
  }
}

/* Loading Animation */
@keyframes iconLoad {
  0% {
    opacity: 0;
    transform: scale(0.8) rotate(-10deg);
  }
  100% {
    opacity: 1;
    transform: scale(1) rotate(0deg);
  }
}

.animated-icon {
  animation: iconLoad 0.6s ease-out;
}

/* Advanced Glow Effect */
.icon-container:hover {
  animation: glowPulse 2s ease-in-out infinite alternate;
}

@keyframes glowPulse {
  0% {
    box-shadow: 
      0 10px 30px rgba(0, 0, 0, 0.3),
      0 0 20px rgba(255, 255, 255, 0.1),
      inset 0 1px 0 rgba(255, 255, 255, 0.2);
  }
  100% {
    box-shadow: 
      0 15px 40px rgba(0, 0, 0, 0.4),
      0 0 30px rgba(255, 255, 255, 0.2),
      inset 0 1px 0 rgba(255, 255, 255, 0.3);
  }
}
</style>
